import Calendar from '@/components/calendar/Calendar';
import { Button } from '@/components/common/Button';
import { Checkbox } from '@/components/common/Checkbox';
import Combobox from '@/components/common/Combobox';
import { Form, FormControl, FormField, FormItem, FormMessage } from '@/components/common/Form';
import { Input } from '@/components/common/Input';
import { Label } from '@/components/common/Label';
import { RadioGroup, RadioGroupItem } from '@/components/common/RadioGroup';
import { BOOK_DAY_IN_ADVANCE, DISCOUNT_RATE, FILE_SIZE_IMAGE, FILE_SIZE_IMAGE_1MB, FORM_TYPE, MAX_PRICE_SERVICE, NEWS_CATEGORY, NEWS_STATUS, NUMBER_OF_VOUCHER, ROUTES, SUPPORTED_FORMATS, USAGE_LIMIT_VOUCHER } from '@/configs/constants';
import { useStore } from '@/hooks/useStore';
import yup from '@/services/yup';
import { NEWS_CATEGORY_TYPE, NEWS_STATUS_TYPE } from '@/types/enums';
import { toastify } from '@/utils/toastify';
import { scrollToTop } from '@/utils/utils';
import { yupResolver } from '@hookform/resolvers/yup';
import { BookMarkedIcon, CircleMinus, CirclePlus, ImagePlusIcon, MessageCircleQuestion, ShieldQuestionIcon, XIcon } from 'lucide-react';
import { flowResult, toJS } from 'mobx';
import { observer } from 'mobx-react-lite';
import React, { useEffect, useState } from 'react';
import { useForm, UseFormReturn } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { t } from 'i18next';
import { title } from 'process';
import { Select, SelectContent, SelectGroup, SelectItem, SelectTrigger, SelectValue } from '@/components/common/Select';
import { Image } from '@/components/common/Image';
import TinyMCEEditor from '@/components/tiny-editor';
import { Description } from '@radix-ui/react-dialog';

const DISCOUNT_TYPE = {
    PERCENT: 'percent',
    FIXED: 'fixed'
};

interface NewsFormProps {
    typeForm: string
}

interface IBanner {
    id: string,
    name: string,
    image: string,
    key?: string,
    isDefault: boolean,
    status: number,
    alt: string,
    createdAt?: string,
    file?: File
}

const validateSchema = yup.object().shape({
    title: yup
        .string()
        .required(t('form_error_validate.required'))
        .max(255, t('form_error_validate.max_length_255', { max: 255 })),

    categoryId: yup.number().required(t('form_error_validate.required')),
    image: yup
        .mixed<File | string>()
        .nullable()
        .test('fileRequired', t('form_error_validate.required'), (value) => {
            if (!value) return false; // nếu bắt buộc phải có ảnh
            if (typeof value === 'string') return true;
            if (value instanceof File) return true;
            return false;
        })
        .test('fileType', t('form_error_validate.topic_image_upload_format'), (value) => {
            if (!value) return true;
            if (typeof value === 'string') return true;
            if (value instanceof File) return SUPPORTED_FORMATS.includes(value.type);
            return false;
        })
        .test('fileSize', t('form_error_validate.topic_image_upload_size_1MB'), (value) => {
            if (!value) return true;
            if (typeof value === 'string') return true;
            if (value instanceof File) return value.size <= FILE_SIZE_IMAGE_1MB;
            return false;
        }),
    imageAlt: yup
        .string()
        .max(255, t('form_error_validate.max_length_255', { max: 255 })),

    descriptionShort: yup.string().required(t('form_error_validate.required')).max(1000, t('form_error_validate.max_length_1000', { max: 1000 })),
    description: yup.string().required(t('form_error_validate.required')),
    publishedDate: yup
        .date()
        .required(t('form_error_validate.required')),

    isHotTopic: yup.boolean().required(t('form_error_validate.required')),
    status: yup.number().required(t('form_error_validate.required'))

});

type FormData = yup.InferType<typeof validateSchema>;

export default observer(function NewsForm({ typeForm }: NewsFormProps) {
    const initialValues = {
        title: '',
        categoryId: 1,
        image: '',
        imageAlt: '',
        descriptionShort: '',
        description: '',
        publishedDate: new Date(new Date().setHours(0, 0, 0, 0)),
        isHotTopic: false,
        status: 1
    };
    const [bannerList, setBannerList] = useState<IBanner[]>([]);
    const labelImagesRef = React.useRef<HTMLLabelElement>(null);
    const [previewImage, setPreviewImage] = useState<string | null>(null);

    const { code } = useParams();
    const { t, i18n: { language } } = useTranslation();

    const form = useForm<FormData>({
        resolver: yupResolver(validateSchema),
        mode: 'onSubmit',
        defaultValues: initialValues
    });

    const {
        modalStore: { showAlertModal, hideModal },
        generalStore: { administrativeUnit },
        newsStore: { createNews,
            newsDetail, getNewsDetail, updateNews, updateNewsStatus }
    } = useStore();
    const navigate = useNavigate();

    const coverImageValue = form.watch('image');

    useEffect(() => {

    }, [coverImageValue]);

    useEffect(() => {
        if ((typeForm === FORM_TYPE.EDIT || typeForm === FORM_TYPE.CLONE) && code) {
            flowResult(getNewsDetail(code));
        }
    }, [code, typeForm]);

    useEffect(() => {
        if (newsDetail) {
            form.reset({
                title: newsDetail.title || '',
                categoryId: newsDetail.categoryId || 0,
                image: Array.isArray(newsDetail.image) ? newsDetail.image[0] : newsDetail.image || undefined,
                imageAlt: newsDetail.imageAlt || '',
                descriptionShort: newsDetail.descriptionShort || '',
                description: newsDetail.description || '',
                publishedDate: newsDetail.publishedDate ? new Date(newsDetail.publishedDate) : new Date(new Date().setHours(0, 0, 0, 0)),
                isHotTopic: newsDetail.isHotTopic || false,
                status: newsDetail.status || 0
            });
        }
    }, [newsDetail]);

    const onsubmit = async (data: FormData) => {
        let hasError = false;

        if (hasError) return;
        const payload = {
            title: data?.title || '',
            categoryId: data?.categoryId || 0,
            image: data?.image ? [data.image] : null,
            imageAlt: data?.imageAlt || '',
            descriptionShort: data?.descriptionShort || '',
            description: data?.description || '',
            publishedDate: data?.publishedDate || '',
            isHotTopic: data?.isHotTopic || false,
            status: data?.status || 0
        };

        if (typeForm === FORM_TYPE.CREATE || typeForm === FORM_TYPE.CLONE) {
            const res = await flowResult(createNews(payload));
            if (res) {
                toastify('success', t('messages.add_success'));
                navigate(`${ROUTES.news.href}/${res.data?.code}`);
            }
        } else if (typeForm === FORM_TYPE.EDIT && code) {
            const res = await flowResult(updateNews(code, payload));
            if (res) {
                toastify('success', t('messages.update_successfully'));
                navigate(`${ROUTES.news.href}`);
            }
        }
    };

    return (
        <>
            <Form {...form}>
                <form onSubmit={form.handleSubmit(onsubmit)} className='pb-5'>
                    <div className='bg-white rounded p-4'>
                        {/* title */}
                        <div className='grid grid-cols-1 gap-x-20 gap-y-4 mb-4'>
                            <FormField
                                control={form.control}
                                name='title'
                                render={({ field }) => (
                                    <FormItem>
                                        <div className='flex items-center'>
                                            <Label className='w-[10%] label-1' required>
                                                {t('words_title.title')}
                                            </Label>
                                            <FormControl className='w-[90%]'>
                                                <Input type='text' {...field} maxLength={255} />
                                            </FormControl>
                                        </div>
                                        <FormMessage className='ml-[10%] mt-1' />
                                    </FormItem>
                                )}
                            />
                        </div>
                        {/* category + cover image */}
                        <div className='grid grid-cols-2 gap-x-20 gap-y-4 mb-4 '>
                            <FormField
                                control={form.control}
                                name='categoryId'
                                render={({ field }) => (
                                    <FormItem>
                                        <div className='flex items-center'>
                                            <Label className='w-1/5 label-1' required>{t('words_title.category')}</Label>
                                            <Select
                                                key={field.value}
                                                onValueChange={val => field.onChange(Number(val))}
                                                value={field.value.toString()}
                                                className='w-4/5 ml-2'
                                            >

                                                <FormControl>
                                                    <SelectTrigger>
                                                        <SelectValue placeholder={t('placeholder.choose')} />
                                                    </SelectTrigger>
                                                </FormControl>
                                                <SelectContent>

                                                    <SelectItem
                                                        className='hover:cursor-pointer'
                                                        value={NEWS_CATEGORY_TYPE.DogCareAndKnowledge.toString()}
                                                    >
                                                        <span>{t('words_title.dog_care_and_knowledge')}</span>
                                                    </SelectItem>
                                                    <SelectItem
                                                        className='hover:cursor-pointer'
                                                        value={NEWS_CATEGORY_TYPE.CatCareAndKnowledge.toString()}
                                                    >
                                                        <span>{t('words_title.cat_care_and_knowledge')}</span>
                                                    </SelectItem>
                                                    <SelectItem
                                                        className='hover:cursor-pointer'
                                                        value={NEWS_CATEGORY_TYPE.VeterinaryGuidesAndExperiences.toString()}
                                                    >
                                                        <span>{t('words_title.veterinary_guides_and_experiences')}</span>
                                                    </SelectItem>

                                                </SelectContent>
                                            </Select>
                                        </div>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />
                            <div className='flex flex-col gap-4'>
                                <FormField
                                    control={form.control}
                                    name='image'
                                    render={({ field }) => (
                                        <FormItem>
                                            <div className='flex items-start'>
                                                <Label className='w-1/5 label-1 pt-3' required>
                                                    {t('words_title.cover_image')}
                                                </Label>
                                                <FormControl className='w-4/5'>
                                                    <div>
                                                        {/* Input ẩn */}
                                                        <Input
                                                            id='cover-image'
                                                            type='file'
                                                            className='hidden'
                                                            accept={SUPPORTED_FORMATS.join(', ')}
                                                            onChange={(event) => {
                                                                const file = event.target.files?.[0];
                                                                if (!file) {
                                                                    event.target.value = '';
                                                                    return;
                                                                }

                                                                const cover = file;

                                                                field.onChange(cover);
                                                            }}
                                                        />

                                                        <div
                                                            className='relative border-2 border-dashed border-rose-300 rounded-md bg-gray-50 h-[150px] w-full text-center hover:bg-gray-100 transition flex flex-col justify-center items-center space-y-2 text-gray-700'
                                                        >
                                                            {field.value ?
                                                                (
                                                                    <div className='mt-3 h-[140px]'>
                                                                        <Image
                                                                            src={
                                                                                typeof field.value === 'string' ?
                                                                                    field.value : // trường hợp load từ API (string URL)
                                                                                    field.value instanceof File ?
                                                                                        URL.createObjectURL(field.value) : // file mới chọn
                                                                                        (field.value as any)?.image // trường hợp bạn custom object
                                                                            }
                                                                            alt='Preview'
                                                                            className='mx-auto h-[128px] rounded '
                                                                        />
                                                                        <button
                                                                            type='button'
                                                                            className='absolute right-1 top-1 bg-white/80 rounded-full p-1'
                                                                            onClick={(e) => {
                                                                                e.stopPropagation();
                                                                                field.onChange(null);
                                                                            }}
                                                                        >
                                                                            <XIcon className='h-4 w-4 text-gray-700' />
                                                                        </button>
                                                                    </div>
                                                                ) :
                                                                (
                                                                    <Label
                                                                        ref={labelImagesRef}
                                                                        htmlFor='cover-image'
                                                                        tabIndex={0}
                                                                        className='cursor-pointer'
                                                                    >

                                                                        <div className='relative h-[140px] flex flex-col items-center justify-center'>
                                                                            <ImagePlusIcon className='text-active h-6 w-6 mr-2' />
                                                                            <span className='text-[16px] text-[var(--active)] mb-2'>{t('button.add_shop_image')}</span>
                                                                            <div className='text-left'>
                                                                                <p className='text-sm'>{t('banner.recommended_ratio')}</p>
                                                                                <p className='text-sm'>{t('banner.file_type')}</p>
                                                                                <p className='text-sm'>{t('banner.max_size')}</p>
                                                                            </div>
                                                                        </div>
                                                                    </Label>
                                                                )}

                                                        </div>

                                                    </div>
                                                </FormControl>
                                            </div>
                                            <FormMessage className='ml-[20%] mt-1' />
                                        </FormItem>
                                    )}
                                />
                                <FormField
                                    control={form.control}
                                    name='imageAlt'
                                    render={({ field }) => (
                                        <FormItem>
                                            <div className='flex items-center'>
                                                <Label className='w-1/5 label-1'>
                                                    {t('words_title.alt_text_cover')}
                                                </Label>
                                                <FormControl className='w-4/5'>
                                                    <Input type='text' {...field} maxLength={255} />
                                                </FormControl>
                                            </div>
                                            <FormMessage className='ml-[10%] mt-1' />
                                        </FormItem>
                                    )}
                                />
                            </div>

                        </div>
                        {/* content short */}
                        <div className='grid grid-cols-1 gap-x-20 gap-y-4 mb-4'>
                            <FormField
                                control={form.control}
                                name='descriptionShort'
                                render={({ field }) => (
                                    <FormItem>
                                        <div className='flex items-center'>
                                            <Label className='w-[10%] label-1' required>
                                                {t('words_title.content_short')}
                                            </Label>
                                            <FormControl className='w-[90%]'>
                                                <Input type='text' {...field} maxLength={1000} />
                                            </FormControl>
                                        </div>
                                        <FormMessage className='ml-[10%] mt-1' />

                                    </FormItem>
                                )}
                            />
                        </div>
                        {/* content full */}
                        <div className='grid grid-cols-1 gap-x-20 gap-y-4 mb-4'>
                            <FormField
                                control={form.control}
                                name='description'
                                render={({ field }) => (
                                    <>
                                        <FormItem className='flex items-start space-y-0'>
                                            <Label className='label-1 w-[10%]' required>{t('words_title.content_full')}</Label>
                                            <div className='w-[90%]'>
                                                <FormControl>
                                                    <div className='h-96'>
                                                        <TinyMCEEditor
                                                            value={field.value}
                                                            onChange={field.onChange}
                                                            placeholder={t('placeholder.click_here_to_enter')}
                                                            height={380}

                                                        />
                                                    </div>
                                                </FormControl>
                                                <FormMessage className='' />
                                            </div>
                                        </FormItem>
                                    </>
                                )}
                            />

                        </div>
                        {/* status + published date */}
                        <div className='grid grid-cols-2 gap-x-20 gap-y-4 mb-4 '>

                            <FormField
                                control={form.control}
                                name='status'
                                render={({ field }) => (
                                    <FormItem className='flex items-center space-y-0'>

                                        <Label className='label-1 w-1/5' required>{t('words_title.status')}</Label>
                                        <FormControl className='w-4/5 ml-2'>
                                            <RadioGroup
                                                className='flex items-center'
                                                onValueChange={(value) => {
                                                    field.onChange(Number(value));
                                                }}
                                                value={field.value.toString()}
                                            // disabled={!!isEndTimePast}
                                            >
                                                <FormItem className='flex w-44 items-center gap-2'>
                                                    <FormControl>
                                                        <RadioGroupItem id='falseRadioStatus' value={NEWS_STATUS_TYPE.Published.toString()} />
                                                    </FormControl>
                                                    <Label
                                                        className='!my-0 cursor-pointer'
                                                        htmlFor='falseRadioStatus'
                                                    >
                                                        {t('words_title.publish')}
                                                    </Label>
                                                </FormItem>
                                                <FormItem className='flex w-44 items-center gap-2'>
                                                    <FormControl>
                                                        <RadioGroupItem id='trueRadioStatus' value={NEWS_STATUS_TYPE.NotPublished.toString()} />
                                                    </FormControl>
                                                    <Label
                                                        className='!my-0 cursor-pointer'
                                                        htmlFor='trueRadioStatus'
                                                    >
                                                        {t('words_title.not_publish')}
                                                    </Label>
                                                </FormItem>

                                            </RadioGroup>
                                        </FormControl>

                                        <div className='flex items-center gap-4'>
                                            <div className='w-1/6'></div>
                                            <FormMessage className='w-5/6' />
                                        </div>
                                    </FormItem>
                                )}
                            />
                            <FormField
                                control={form.control}
                                name='publishedDate'
                                render={({ field }) => (
                                    <FormItem className='flex items-center space-y-0'>
                                        <Label className='label-1 w-1/5' required>{t('words_title.published_date')}</Label>
                                        <div className='w-4/5'>
                                            <FormControl>
                                                <Calendar
                                                    mode='single'
                                                    selected={field.value}
                                                    onSelect={field.onChange}
                                                    disabled={(date) => {
                                                        return false;
                                                    }}
                                                    placeholder=''
                                                    useIconRemove={true}
                                                    className='w-full'

                                                    required
                                                />
                                            </FormControl>

                                        </div>

                                    </FormItem>
                                )}
                            />
                        </div>
                        {/* hot topic */}
                        <div className='grid grid-cols-2 gap-x-20 gap-y-4 mb-4 bg-[#f5f5f5] p-[14px]'>
                            <FormField
                                control={form.control}
                                name='isHotTopic'
                                render={({ field }) => (
                                    <FormItem>
                                        <div className='flex gap-2 items-center'>
                                            <FormControl>
                                                <Checkbox
                                                    className='first:border-[var(--active)]'
                                                    checked={field.value}
                                                    onCheckedChange={field.onChange}
                                                />
                                            </FormControl>
                                            <Label>{t('words_title.tick_if_you_want_to_display_this_article_in_hot_topic')}</Label>
                                        </div>
                                    </FormItem>
                                )}
                            />
                        </div>

                    </div>
                    <div className='bg-white rounded p-4 mt-4'>
                        <div className='flex justify-center'>
                            <button
                                type='button'
                                className='bg-[#EBEBEB] text-[#000] cursor-pointer w-[200px] h-[40px] leading-[40px] text-center rounded-sm mr-[30px]'
                                onClick={() => {
                                    navigate(ROUTES.news.href);
                                }}
                            >
                                {t('words_title.cancel')}
                            </button>

                            <button type='submit' className='bg-[#9A6051] text-[#fff] cursor-pointer w-[200px] h-[40px] leading-[40px] text-center rounded-sm'>
                                {t('words_title.save')}
                            </button>
                        </div>
                    </div>
                </form>
            </Form>

        </>
    );
});
